// Quick-view modal — hijacks clicks on product cards so visitors get
// a fast preview (image, title, price, description, related products)
// without leaving the listing page they're on. "View Details" inside
// the modal still takes them to the PDP for full purchase flow.
//
// Trigger: any `<a>` inside a `.product-quick` card. We listen at the
// document level so it works on cards added later (e.g. lazy-loaded
// shop archive, related-products row inside the modal itself).
//
// Open paths:
//   - Homepage Weekly Offers grid
//   - Shop archive (PLP)
//   - Single product → "Similar Products" row at the bottom
//   - Inside the modal: clicking a related-product card re-opens the
//     modal with that product instead of navigating

(function () {
    const HAEGOZIYA = window.HAEGOZIYA || {};
    const ajaxUrl  = HAEGOZIYA.ajaxUrl || '/wp-admin/admin-ajax.php';
    const nonce    = HAEGOZIYA.quickviewNonce;
    if (!nonce) return; // hard guard — endpoint is nonce-gated

    const modal = document.querySelector('[data-quickview-modal]');
    if (!modal) return;

    // ── Modal slot refs (resolved once, reused per open) ───────────
    const els = {
        body:        modal.querySelector('[data-quickview-body]'),
        image:       modal.querySelector('[data-quickview-image]'),
        thumbs:      modal.querySelector('[data-quickview-thumbs]'),
        title:       modal.querySelector('[data-quickview-title]'),
        price:       modal.querySelector('[data-quickview-price]'),
        perUnit:     modal.querySelector('[data-quickview-per-unit]'),
        tiers:       modal.querySelector('[data-quickview-tiers]'),
        description: modal.querySelector('[data-quickview-description]'),
        viewDetails: modal.querySelector('[data-quickview-view-details]'),
        buyNow:      modal.querySelector('[data-quickview-buy-now]'),
        qty:         modal.querySelector('[data-quickview-qty-control]'),
        relatedWrap: modal.querySelector('[data-quickview-related-wrap]'),
        relatedList: modal.querySelector('[data-quickview-related]'),
    };

    // Holds the current product context so Buy Now knows what to add
    // without re-reading from the qty control's dataset every click.
    let currentProduct = null;

    let lastFocusedTrigger = null;
    let abortController = null;

    // ── Card-click handler ─────────────────────────────────────────
    document.addEventListener('click', (e) => {
        // Find a candidate trigger: a link inside a product card OR a
        // related-product card inside this same modal.
        const link = e.target.closest('a');
        if (!link) return;
        const card = link.closest('.product-quick, [data-quickview-related-card]');
        if (!card) return;

        // Skip middle-click / cmd-click / ctrl-click — let those open
        // the PDP in a new tab as the user expects.
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

        // Don't hijack the in-card controls: +/- quick-add, carousel dots,
        // or the wishlist heart (which lives INSIDE the media <a>, so a click
        // on it resolves to that <a> and would otherwise open the modal).
        if (e.target.closest('[data-qty-plus], [data-qty-minus], [data-carousel-dot], [data-wishlist-toggle], .product-quick__wishlist')) return;

        const productId = parseInt(card.dataset.productId || '0', 10);
        if (!productId) return;

        e.preventDefault();
        lastFocusedTrigger = link;
        open(productId);
    });

    // ── Close handlers ─────────────────────────────────────────────
    modal.addEventListener('click', (e) => {
        if (e.target.closest('[data-quickview-close]')) {
            e.preventDefault();
            close();
        }
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modal.hidden) close();
    });

    // ── Open: fetch + render + show ─────────────────────────────────
    async function open(productId) {
        // If the side-cart drawer is open (e.g. user clicked a bundle item
        // inside it), close it first — the drawer sits above this modal.
        document.dispatchEvent(new CustomEvent('haegoziya:close-cart-drawer'));

        // Show modal in a loading shell so users get instant feedback
        // even on slow connections.
        renderLoading();
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('qv-modal-open');

        // Cancel any in-flight request from a prior trigger.
        if (abortController) abortController.abort();
        abortController = new AbortController();

        try {
            const data = await fetchQuickView(productId, abortController.signal);
            renderProduct(data);
        } catch (err) {
            if (err && err.name === 'AbortError') return;
            // Bail to the PDP — never strand the user in a broken modal.
            if (lastFocusedTrigger && lastFocusedTrigger.href) {
                window.location.href = lastFocusedTrigger.href;
            } else {
                close();
            }
        }
    }

    function close() {
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('qv-modal-open');
        if (abortController) { abortController.abort(); abortController = null; }
        if (lastFocusedTrigger) {
            try { lastFocusedTrigger.focus({ preventScroll: true }); } catch (_) {}
            lastFocusedTrigger = null;
        }
    }

    // ── Render: loading state ───────────────────────────────────────
    function renderLoading() {
        els.title.textContent = '';
        els.price.innerHTML = '';
        els.description.innerHTML = '';
        els.image.removeAttribute('src');
        els.image.alt = '';
        els.thumbs.innerHTML = '';
        if (els.perUnit) els.perUnit.hidden = true;
        if (els.tiers) { els.tiers.innerHTML = ''; els.tiers.hidden = true; }
        clearStashedTier();
        els.relatedList.innerHTML = '';
        els.relatedWrap.hidden = true;
        els.body.classList.add('is-loading');
    }

    // ── Render: full product payload ────────────────────────────────
    function renderProduct(data) {
        els.body.classList.remove('is-loading');

        // Stash for the Buy Now click handler.
        currentProduct = data;

        // Title + price + description
        els.title.textContent = data.title || '';
        els.price.innerHTML = data.price_html || '';
        els.description.innerHTML = data.description || '';

        // Images: first is main, rest are thumbs
        const images = Array.isArray(data.images) && data.images.length ? data.images : [''];
        setMainImage(images[0], data.title);
        els.thumbs.innerHTML = '';
        if (images.length > 1) {
            images.forEach((src, idx) => {
                const li = document.createElement('li');
                li.className = 'qv-modal__thumb' + (idx === 0 ? ' is-active' : '');
                li.innerHTML =
                    `<button type="button" data-quickview-thumb-btn aria-label="Image ${idx + 1}">
                        <img src="${escapeAttr(src)}" alt="" loading="lazy" decoding="async">
                    </button>`;
                li.querySelector('button').addEventListener('click', () => {
                    setMainImage(src, data.title);
                    els.thumbs.querySelectorAll('.qv-modal__thumb').forEach((el) => {
                        el.classList.toggle('is-active', el === li);
                    });
                });
                els.thumbs.appendChild(li);
            });
        }

        // View Details → PDP
        els.viewDetails.href = data.permalink || '#';
        if (data.labels && data.labels.view_details) {
            // Replace the textNode (preserve the trailing arrow SVG).
            const firstText = els.viewDetails.childNodes[0];
            if (firstText && firstText.nodeType === Node.TEXT_NODE) {
                firstText.textContent = data.labels.view_details + ' ';
            }
        }

        // Buy Now button — enabled only when the product is in stock.
        if (els.buyNow) {
            els.buyNow.disabled = !data.in_stock;
            els.buyNow.classList.remove('is-loading');
            const buyLabel = els.buyNow.querySelector('span');
            if (buyLabel && data.labels && data.labels.buy_now) {
                buyLabel.textContent = data.labels.buy_now;
            }
        }

        // Wire qty control to this product so the modal's +/- act on
        // the real cart (uses the same data-qty-control protocol as
        // the homepage cards — see quick-add.js).
        if (els.qty) {
            els.qty.dataset.productId   = String(data.id || 0);
            els.qty.dataset.variationId = String(data.default_variation_id || 0);
            els.qty.dataset.attributes  = JSON.stringify(data.default_attributes || {});
            const plus  = els.qty.querySelector('[data-qty-plus]');
            const minus = els.qty.querySelector('[data-qty-minus]');
            const count = els.qty.querySelector('[data-qty-count]');
            if (count) count.textContent = '0';
            if (plus)  plus.disabled  = !data.in_stock;
            if (minus) minus.disabled = true;
            // Tell quick-add.js to re-bind on this control (it normally
            // only binds on initial page load).
            els.qty.dispatchEvent(new CustomEvent('haegoziya:rebind-qty', { bubbles: true }));
        }

        // Weight tiers (300g / 500g …) — render the same chips as the card.
        renderTiers(data.tiers || [], (data.labels && data.labels.price_per_100) || '');

        // Related products
        renderRelated(data.related || [], (data.labels && data.labels.related_title) || '');
    }

    // ── Render: weight tier chips ──────────────────────────────────
    // Mirrors the product card (assets/src/js/modules/tier-chips.js): a row
    // of chips where the active one drives the displayed price + the weight
    // sent to the cart. The chosen tier is stashed on the qty control so the
    // modal's +/- (handled by quick-add.js) adds it as its own cart line.
    function renderTiers(tiers, perUnitLabel) {
        if (!els.tiers) return;
        els.tiers.innerHTML = '';
        clearStashedTier();
        if (els.perUnit) els.perUnit.hidden = true;

        if (!Array.isArray(tiers) || !tiers.length) {
            els.tiers.hidden = true;
            return;
        }
        els.tiers.hidden = false;

        let defaultIdx = tiers.findIndex((t) => t && t.default);
        if (defaultIdx < 0) defaultIdx = 0;

        tiers.forEach((t, i) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'tier-chip' + (i === defaultIdx ? ' is-active' : '');
            btn.dataset.tierIndex  = String(i);
            btn.dataset.tierPrice  = String(t.price  != null ? t.price  : '');
            btn.dataset.tierWeight = String(t.weight != null ? t.weight : '');
            btn.dataset.tierLabel  = String(t.label  != null ? t.label  : '');
            btn.textContent = t.label || '';
            btn.addEventListener('click', () => selectTier(tiers, i, perUnitLabel, false));
            els.tiers.appendChild(btn);
        });

        // Apply the default silently (don't reset a count the user hasn't set).
        selectTier(tiers, defaultIdx, perUnitLabel, true);
    }

    function selectTier(tiers, idx, perUnitLabel, silent) {
        const t = tiers[idx];
        if (!t) return;

        els.tiers.querySelectorAll('.tier-chip').forEach((c, i) => {
            c.classList.toggle('is-active', i === idx);
        });

        // Price → this tier's price, formatted like Woo's "16.00 ₪".
        if (t.price) {
            els.price.innerHTML =
                `<span class="woocommerce-Price-amount amount"><bdi>${formatPrice(t.price)}</bdi></span>`;
        }

        // Price-per-100g line.
        if (els.perUnit && t.weight && t.price && perUnitLabel) {
            const per100 = (parseFloat(t.price) / parseFloat(t.weight) * 100).toFixed(2);
            els.perUnit.textContent = perUnitLabel.replace('%s', per100);
            els.perUnit.hidden = false;
        } else if (els.perUnit) {
            els.perUnit.hidden = true;
        }

        // Stash the chosen tier on the qty control so quick-add.js sends it.
        els.qty.dataset.activeTierIndex  = String(idx);
        els.qty.dataset.activeTierPrice  = String(t.price  != null ? t.price  : '');
        els.qty.dataset.activeTierWeight = String(t.weight != null ? t.weight : '');
        els.qty.dataset.activeTierLabel  = String(t.label  != null ? t.label  : '');

        // Switching weight starts a fresh line — reset the stepper to 0.
        if (!silent) {
            els.qty.dispatchEvent(new CustomEvent('haegoziya:rebind-qty', { bubbles: true }));
        }
    }

    function clearStashedTier() {
        if (!els.qty) return;
        delete els.qty.dataset.activeTierIndex;
        delete els.qty.dataset.activeTierPrice;
        delete els.qty.dataset.activeTierWeight;
        delete els.qty.dataset.activeTierLabel;
    }

    function formatPrice(price) {
        const num = parseFloat(price);
        if (isNaN(num)) return '';
        const cur = (window.HAEGOZIYA && window.HAEGOZIYA.currency) || '₪';
        return `${num.toFixed(2)} ${cur}`;
    }

    function renderRelated(items, label) {
        if (!items.length) {
            els.relatedWrap.hidden = true;
            return;
        }
        els.relatedWrap.hidden = false;
        const titleEl = els.relatedWrap.querySelector('.qv-modal__related-title');
        if (titleEl && label) titleEl.textContent = label;

        els.relatedList.innerHTML = '';
        items.forEach((item) => {
            const li = document.createElement('li');
            li.className = 'qv-modal__related-card';
            li.dataset.productId = String(item.id || 0);
            li.setAttribute('data-quickview-related-card', '');
            li.innerHTML = `
                <a href="${escapeAttr(item.permalink || '#')}" class="qv-modal__related-link">
                    <span class="qv-modal__related-media">
                        <img src="${escapeAttr(item.image || '')}" alt="${escapeAttr(item.title || '')}" loading="lazy" decoding="async">
                    </span>
                    <span class="qv-modal__related-title-text">${escapeHtml(item.title || '')}</span>
                    <span class="qv-modal__related-price">${item.price_html || ''}</span>
                </a>
            `;
            els.relatedList.appendChild(li);
        });
    }

    function setMainImage(src, alt) {
        if (!src) return;
        els.image.src = src;
        els.image.alt = alt || '';
    }

    // ── Buy Now ────────────────────────────────────────────────────
    // Adds the current product to the cart with the qty the user picked
    // in the modal (defaults to 1 if they haven't touched the stepper)
    // then redirects to /cart/ so they can proceed straight to checkout.
    if (els.buyNow) {
        els.buyNow.addEventListener('click', async (e) => {
            e.preventDefault();
            if (!currentProduct || !currentProduct.in_stock) return;
            if (els.buyNow.classList.contains('is-loading')) return;

            // Read whatever qty the user picked in the modal; default 1.
            const countEl = els.qty && els.qty.querySelector('[data-qty-count]');
            const picked  = countEl ? (parseInt(countEl.textContent, 10) || 0) : 0;
            const quantity = Math.max(1, picked);

            els.buyNow.classList.add('is-loading');
            els.buyNow.disabled = true;

            try {
                const body = new URLSearchParams();
                body.set('action', 'haegoziya_set_qty');
                body.set('nonce', HAEGOZIYA.qtyNonce || '');
                body.set('product_id', String(currentProduct.id || 0));
                body.set('variation_id', String(currentProduct.default_variation_id || 0));
                body.set('quantity', String(quantity));
                body.set('mode', 'add');         // ADD to cart, don't SET — keeps existing items intact
                if (currentProduct.default_attributes) {
                    for (const [k, v] of Object.entries(currentProduct.default_attributes)) {
                        body.set('attributes[' + k + ']', String(v));
                    }
                }
                // Carry the selected weight tier (if any) so Buy Now adds the
                // right weight at the right price — matching the +/- stepper.
                if (els.qty && els.qty.dataset.activeTierIndex !== undefined && els.qty.dataset.activeTierIndex !== '') {
                    body.set('tier_index',  String(els.qty.dataset.activeTierIndex));
                    body.set('tier_price',  String(els.qty.dataset.activeTierPrice  || ''));
                    body.set('tier_weight', String(els.qty.dataset.activeTierWeight || ''));
                    body.set('tier_label',  String(els.qty.dataset.activeTierLabel  || ''));
                }

                const res = await fetch(ajaxUrl, {
                    method: 'POST',
                    body,
                    credentials: 'same-origin',
                    cache: 'no-store',
                });
                if (!res.ok) throw new Error('buy_now HTTP ' + res.status);
                const json = await res.json();
                if (!json || !json.success) throw new Error('buy_now rejected');

                // Redirect to the cart so the user can confirm + check out.
                window.location.href = HAEGOZIYA.cartUrl || '/cart/';
            } catch (err) {
                els.buyNow.classList.remove('is-loading');
                els.buyNow.disabled = !currentProduct.in_stock;
                // Surface a soft error via the modal title region — no
                // toast plumbing yet, and silent failure would be worse.
                console.error('[quickview] Buy Now failed:', err);
            }
        });
    }

    // ── AJAX ────────────────────────────────────────────────────────
    async function fetchQuickView(productId, signal) {
        const body = new URLSearchParams();
        body.set('action', 'haegoziya_quickview');
        body.set('nonce', nonce);
        body.set('product_id', String(productId));

        const res = await fetch(ajaxUrl, {
            method: 'POST',
            body,
            signal,
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) throw new Error('quickview HTTP ' + res.status);
        const json = await res.json();
        if (!json || !json.success || !json.data) throw new Error('quickview payload invalid');
        return json.data;
    }

    // ── Utility: safe HTML/attr escapes ─────────────────────────────
    function escapeHtml(s) {
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }
    function escapeAttr(s) {
        return escapeHtml(s).replace(/"/g, '&quot;');
    }
})();
